import { types } from "../types/types";
import jwt_decode from "jwt-decode";
import { importNewModal } from "./newModal";
import { clearCompanyListCache } from "../../utils/companyListCache";
import { clearSessionBannerDismissals } from "../../utils/bannerSessionDismissals";
import i18n from "../../i18n";
import {
  APP_LANGUAGE_STORAGE_KEY,
  isAppLanguage,
  writeStoredAppLanguage,
} from "../../utils/appLanguage";
import { identifyHeapUser, resetHeapIdentity } from "../../utils/heapAnalytics";
const login = (user) => ({
  type: types.authLogin,
  payload: user,
});

const updateCashflow = (cashflowCategories) => ({
  type : types.updateCashflowCollapsedCategories,
  payload: cashflowCategories
})

const firstTimeOnApp = (payload) => ({
  type : types.authFirstTime,
  payload
})

const showWelcomeModal = (payload) => ({
  type : types.authShowWelcomeModal,
  payload
})

export const UpdateCashflowCategories = (cashflow) => {
  return async (dispatch) => {
    dispatch(updateCashflow(cashflow))
  }
}

export const setFirstTimeOnApp = (payload) => {
  return async (dispatch) => {
    dispatch(firstTimeOnApp(payload))
  }
}

export const setShowWelcomeModal = (payload) => {
  return async (dispatch) => {
    dispatch(showWelcomeModal(payload))
  }
}

const updateNominasFilter = (filter) => ({
  type: types.updateNominasFilter,
  payload: filter
})

export const updateNominaFilterByUser = (filter) => {
  return async (dispatch) => {
    dispatch(updateNominasFilter(filter))
  }
}

export const startChecking = () => {
  return async (dispatch) => {
    let user = JSON.parse(localStorage.getItem("user"));

    if (!!user?.uid) {
      dispatch(importNewModal({ show: false }))
    }

    if (user) {
      dispatch(
        login({
          uid: user.uid,
          name: user.name,
          surname: user.surname,
          avatarKey: user.avatarKey || null,
          email: user.email,
          // Rehidratar también los campos de sesión que startLogin persiste,
          // para que tras un F5 (sin nuevo login) Redux conserve roles, etc.
          // OJO: NO rehidratamos modulePermissions/blockedFeatures a propósito;
          // esos vienen siempre frescos vía GetMyPermissions (Layout).
          roles: user.roles,
          sessionExp: user.sessionExp,
          flowView: user.flowView,
          cashflowCollapsedCategories: user.cashflowCollapsedCategories,
          nominasLabelFilter: user.nominasLabelFilter,
          preferredLanguage: user.preferredLanguage,
        })
      );
      if (isAppLanguage(user.preferredLanguage)) {
        writeStoredAppLanguage(user.preferredLanguage);
        if (i18n.language !== user.preferredLanguage) {
          i18n.changeLanguage(user.preferredLanguage);
        }
      }
    }
  };
};

/**
 * @param {string|{id:string,firstName?:string,lastName?:string,email?:string,avatar?:string,roles?:string[],exp?:number}} tokenOrSession
 *   JWT (legacy) o sessionUser del login con cookie httpOnly.
 * @param {boolean} [skipRedirect=false] true en onboarding / auto-login que navegan ellos
 * @param {{ replace?: (path: string) => void; push?: (path: string) => void } | null} [historyRef] history de react-router v5
 */
export const startLogin = (tokenOrSession, skipRedirect = false, historyRef = null) => {
  return async (dispatch) => {
    if (!tokenOrSession) return;

    let userData;

    if (typeof tokenOrSession === "string") {
      const { user, exp } = jwt_decode(tokenOrSession);
      identifyHeapUser({
        uid: user.id,
        email: user.emails[0].address,
      });
      userData = {
        uid: user.id,
        name: user.profile.firstName,
        surname: user.profile.lastName,
        avatarKey: user.profile?.avatar || null,
        email: user.emails[0].address,
        roles: user.roles,
        sessionExp: exp,
        flowView: user.flowView || [{}],
        cashflowCollapsedCategories: user.cashflowCollapsedCategories || [],
        nominasLabelFilter: user?.nominasLabelFilter,
        preferredLanguage: user?.preferredLanguage,
        modulePermissions: user.modulePermissions || [],
        blockedFeatures: user.blockedFeatures || [],
      };
      if (isAppLanguage(user?.preferredLanguage)) {
        writeStoredAppLanguage(user.preferredLanguage);
        if (i18n.language !== user.preferredLanguage) {
          i18n.changeLanguage(user.preferredLanguage);
        }
      }
    } else {
      const s = tokenOrSession;
      identifyHeapUser({
        uid: s.id,
        email: s.email,
      });
      userData = {
        uid: s.id,
        name: s.firstName || "",
        surname: s.lastName || "",
        avatarKey: s.avatar || null,
        email: s.email,
        roles: s.roles || [],
        sessionExp: s.exp,
        flowView: [{}],
        cashflowCollapsedCategories: [],
        nominasLabelFilter: undefined,
        preferredLanguage: undefined,
        modulePermissions: [],
        blockedFeatures: [],
      };
    }

    // JWT stays in httpOnly cookie — do not keep it in localStorage.
    try {
      localStorage.removeItem("token");
    } catch (_) {
      /* ignore */
    }

    // No persistimos modulePermissions/blockedFeatures en localStorage para evitar
    // permisos stale entre recargas. La fuente de verdad es GetMyPermissions (Layout).
    const { modulePermissions: _mp, blockedFeatures: _bf, ...persistedUserData } = userData;
    localStorage.setItem("user", JSON.stringify(persistedUserData));
    clearSessionBannerDismissals();

    dispatch(login(userData));

    if (!skipRedirect && historyRef) {
      if (typeof historyRef.replace === "function") {
        historyRef.replace("/");
      } else if (typeof historyRef.push === "function") {
        historyRef.push("/");
      }
    }
  };
};

export const startLogout = () => {
  return (dispatch) => {
    resetHeapIdentity();
    clearCompanyListCache();
    clearSessionBannerDismissals();

    // Clear httpOnly session cookie (best-effort; hard navigate follows).
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      if (apiUrl) {
        fetch(apiUrl, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            query: "mutation { logout }",
          }),
        }).catch(() => {});
      }
    } catch (_) {
      /* ignore */
    }

    const savedLanguage = localStorage.getItem(APP_LANGUAGE_STORAGE_KEY);
    localStorage.clear();
    if (savedLanguage) {
      localStorage.setItem(APP_LANGUAGE_STORAGE_KEY, savedLanguage);
    }
    localStorage.removeItem("token");
    dispatch(logout());

    // Recarga dura obligatoria: el cliente urql es un singleton de módulo y su
    // authExchange retiene el token en memoria (authState). Limpiar localStorage
    // y Redux NO lo borra, así que sin recargar el próximo login en la misma
    // pestaña seguiría enviando el Authorization del usuario anterior (fuga de
    // sesión entre usuarios). El reload destruye el cliente y su authState.
    if (typeof window !== "undefined" && window.location) {
      window.location.assign("/signin");
    }
  };
};

const logout = () => ({ type: types.authLogout });

// Acción para actualizar permisos de módulos en tiempo real (sin re-login)
const updatePermissions = (permissions) => ({
  type: types.updateModulePermissions,
  payload: permissions,
});

export const updateModulePermissions = (permissionsResponse) => {
  return (dispatch) => {
    // permissionsResponse ahora tiene { modulePermissions, blockedFeatures }
    const modulePermissions = permissionsResponse?.modulePermissions || [];
    const blockedFeatures = permissionsResponse?.blockedFeatures || [];

    dispatch(updatePermissions({ modulePermissions, blockedFeatures }));

    // No persistimos en localStorage: los permisos viven sólo en Redux (estado vivo).
    // Esto evita falsos bloqueos por caché viejo cuando admin cambia permisos y el
    // usuario no recarga. Limpiamos también cualquier residuo persistido previamente.
    try {
      const stored = JSON.parse(localStorage.getItem("user") || "null");
      if (stored && ("modulePermissions" in stored || "blockedFeatures" in stored)) {
        delete stored.modulePermissions;
        delete stored.blockedFeatures;
        localStorage.setItem("user", JSON.stringify(stored));
      }
    } catch (_) {
      // ignorar errores de parseo
    }
  };
};
